import { RulerDimensionLine } from 'lucide-react';
import React, { useState, useEffect, useRef } from "react";

interface RangeSliderProps {
    filter: any;
    onChange: any;
    handleFinalAction?: any;
    step?: number;
    className?: string;
    hideInfo?: boolean;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({ filter, onChange, className, hideInfo, handleFinalAction }) => {
    const [minValOption, setMinValOption] = useState(filter.options[0][0]);
    const [maxValOption, setMaxValOption] = useState(filter.options[0][1]);
    const [minVal, setMinVal] = useState(filter.value[0]);
    const [maxVal, setMaxVal] = useState(filter.value[1]);
    const minRef = useRef<HTMLInputElement>(null);
    const maxRef = useRef<HTMLInputElement>(null);
    const range = useRef<HTMLDivElement>(null);

    // Sync minValOption and maxValOption with filter.value changes
    useEffect(() => {
        setMinVal(filter.value[0]);
        setMaxVal(filter.value[1]);
    }, [filter.value]);

    // Convert to percentage
    const getPercent = (value: number) =>
        Math.round(((value - filter.options[0][0]) / (filter.options[0][1] - filter.options[0][0])) * 100);

    // Set width of the range to decrease from the left side
    useEffect(() => {
        if (maxRef.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(maxVal); // Precompute maxPercent

            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, maxVal, filter.options]); // Added maxVal to dependencies for safety

    // Set width of the range to decrease from the right side
    useEffect(() => {
        if (minRef.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(maxVal);

            if (range.current) {
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, minVal, filter.options]);


    const handleClearRange = () => {
        const defaultMin = filter.options[0][0];
        const defaultMax = filter.options[0][1];
        setMinVal(defaultMin);
        setMaxVal(defaultMax);
        setMinValOption(defaultMin);
        setMaxValOption(defaultMax);
        if (filter.clear) {
            filter.clear();
        }
    };

    // const isRangeModified = minVal !== filter.options[0][0] || maxVal !== filter.options[0][1];
    const isRangeModified = minVal !== minValOption || maxVal !== maxValOption;

    return (
        <div className={"flex flex-col w-full " + (className ? className : '')}>
            {isRangeModified && (
                <div className="flex justify-start mb-2">
                    <button
                        onClick={handleClearRange}
                        className="text-gray-600 hover:text-gray-800 cursor-pointer underline"
                        style={{
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            fontSize: '10px',
                            lineHeight: '100%',
                            letterSpacing: '0.5%',
                            textDecorationStyle: 'solid',
                            textDecorationSkipInk: 'auto'
                        }}
                    >
                        Clear range
                    </button>
                </div>
            )}
            <div className="flex mb-2">
                <div><span className="text-gray-900">
                    {filter.formatLabel ? filter.formatLabel(minVal) : (filter.minLabel || minVal)}
                </span></div>
                <div className="flex-1"></div>
                <div><span className="text-gray-900">
                    {filter.formatLabel ? filter.formatLabel(maxVal) : (filter.maxLabel || maxVal)}
                </span></div>
            </div>
            <div className="relative w-full h-2 bg-gray-200 rounded-lg">
                <div
                    ref={range}
                    className="absolute h-2 bg-blue-500 rounded-lg"
                    style={{ zIndex: 1 }}
                ></div>
                <input
                    type="range"
                    // min={minValOption}
                    //  max={maxValOption}

                    min={filter.options[0][0]}
                    max={filter.options[0][1]}
                    value={minVal}
                    ref={minRef}
                    step={filter.step}
                    onChange={(event) => {
                        const value = Math.min(+event.target.value, maxVal - (filter.step || 1));
                        setMinVal(value);
                        onChange(filter, [value, maxVal]);
                        event.target.value = value.toString();
                    }}
                    onMouseUp={() => {
                        if (handleFinalAction) {
                            handleFinalAction(filter, [minVal, maxVal]);
                        }
                    }}
                    onTouchEnd={() => {
                        if (handleFinalAction) {
                            handleFinalAction(filter, [minVal, maxVal]);
                        }
                    }}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    style={{ zIndex: 3 }}
                />
                <input
                    type="range"
                    // min={minValOption}
                    // max={maxValOption}

                    min={filter.options[0][0]}
                    max={filter.options[0][1]}
                    value={maxVal}
                    ref={maxRef}
                    step={filter.step}
                    onChange={(event) => {
                        const value = Math.max(+event.target.value, minVal + (filter.step || 1));
                        setMaxVal(value);
                        onChange(filter, [minVal, value]);
                        event.target.value = value.toString();
                    }}
                    onMouseUp={() => {
                        if (handleFinalAction) {
                            handleFinalAction(filter, [minVal, maxVal]);
                        }
                    }}
                    onTouchEnd={() => {
                        if (handleFinalAction) {
                            handleFinalAction(filter, [minVal, maxVal]);
                        }
                    }}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    style={{ zIndex: 4 }}
                />
            </div>
            {(hideInfo == undefined || !hideInfo) && <div className="flex text-xs mt-2">
                <RulerDimensionLine className="text-gray-500 mr-1" width={16} height={16} /> <span>Update units in <a className="underline" href="/auth/edit">your profile</a>.</span>
            </div>
            }
        </div>
    );
};